import { Keys } from './Keys';

const instances = new Map<number, Modifiers>();

/**
 * Represents a single state of the three modifier keys; ctrl, alt and shift.
 */
export class Modifiers {

    /**
     * An object representing no active modifiers.
     */
    public static readonly none = Modifiers.fromValues(false, false, false);

    /**
     * Obtains a Modifiers object that represents the modifier states specified as parameters.y
     *
     * @param ctrl
     * @param alt
     * @param shift
     */
    public static fromValues(ctrl: boolean, alt: boolean, shift: boolean) {
        const key = (ctrl ? 100 : 0) + (alt ? 10 : 0) + (shift ? 1 : 0);
        let inst = instances.get(key);
        if (!inst) instances.set(key, inst = new Modifiers(ctrl, alt, shift));
        return inst;
    }

    /**
     * Obtains a Modifiers object that represents any modifiers specified in the key array.
     *
     * @param keys
     */
    public static fromKeys(keys: number[]): Modifiers {
        return this.fromValues(
            !!keys.find(x => x === Keys.CTRL),
            !!keys.find(x => x === Keys.ALT),
            !!keys.find(x => x === Keys.SHIFT)
        );
    }

    /**
     * Obtains a Modifiers object that represents the modifier state in the specified event.
     *
     * @param e
     */
    public static fromEvent(e: MouseEvent | KeyboardEvent): Modifiers {
        return new Modifiers(e.ctrlKey || e.metaKey, e.altKey, e.shiftKey);
    }

    /**
     * Parses the specified key expression and returns the modifiers it specifies.
     *
     * @param expr
     */
    public static parse(expr: string): Modifiers {
        const keys = (expr || '')
            .split(/[\s\-+]+/)
            .filter(x => !!x)
            .map(x => Keys.parse(x));

        return this.fromValues(
            keys.some(x => x === Keys.CTRL),
            keys.some(x => x === Keys.ALT),
            keys.some(x => x === Keys.SHIFT)
        );
    }

    /**
     * Returns a value indicating whether or not the specified key is a modifier key.
     *
     * @param key the key.
     */
    public static is(key: number): boolean {
        return key === Keys.CTRL || key === Keys.ALT || key === Keys.SHIFT;
    }

    public readonly ctrl: boolean;
    public readonly alt: boolean;
    public readonly shift: boolean;

    private constructor(ctrl: boolean, alt: boolean, shift: boolean) {
        this.ctrl = !!ctrl;
        this.alt = !!alt;
        this.shift = !!shift;
    }

    /**
     * Returns a value indicating whether any modifier key is pressed.
     */
    public get any() {
        return this.alt || this.ctrl || this.shift;
    }

    /**
     * Tests whether this modifier state matches the specified other modifier state.  When exact=true (default) the
     *  match includes false values, otherwise only true values are considered.
     *
     * @param other
     */
    public matches(other: Modifiers, exact: boolean = true): boolean {
        if (this.ctrl || exact) {
            if (this.ctrl !== other.ctrl) return false;
        }
        if (this.alt || exact) {
            if (this.alt !== other.alt) return false;
        }
        if (this.shift || exact) {
            if (this.shift !== other.shift) return false;
        }
        return true;
    }

    public toString() {
        return JSON.stringify(this);
    }
}
